"use client"
import React, { useCallback, useEffect, useState } from 'react'
import { addUserRole, fetchUsers, getBaseList, removeUserRole, setUserActive } from '@/modules/users/infraestructure/usersRequests'
import Table from '@/modules/users/app/components/UsersTable'
import EditUserDrawer from '@/modules/users/app/components/EditUserDrawer'
import { IUser, IUserBase } from '@/modules/users/domain/IUser';
import swal from 'sweetalert'
import UsersTableFilters from '@/modules/users/app/components/UsersTableFilters'
import { IUserFilters } from '@/modules/users/domain/UsersTable'
import { errorHandler } from '@/config/errorReporting'
import { auth } from '@/config/firebaseApp';
import { User } from 'firebase/auth';

export default function UsersView({
	isAdmin,
	isSoporte,
	puedeEditarMatricula,
}: {
	isAdmin?: boolean;
	isSoporte?: boolean;
	/** Admin u owner: es lo que acepta la API que escribe la matrícula. */
	puedeEditarMatricula?: boolean;
}) {
  const [users, setUsers] = useState<IUser[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  
  const filtersInitialState: IUserFilters = {
		fullname: "",
  };
  
  const [filters, setFilters] = useState<IUserFilters>(filtersInitialState)
  // Se incrementa para forzar un refetch después de dar de baja o rehabilitar,
  // sin tocar los filtros (que resetearían la página).
  const [refreshToken, setRefreshToken] = useState(0);
  const [editandoUid, setEditandoUid] = useState<string | null>(null);
  const [bases, setBases] = useState<IUserBase[]>([]);

  // Las bases se piden una vez: el drawer las necesita para el selector.
  useEffect(() => {
    if (!auth?.currentUser) return;
    getBaseList({ currentUser: auth.currentUser })
      .then(setBases)
      .catch((err) => errorHandler?.report(`Error obteniendo bases: ${err}`));
  }, [refreshToken]);

  // Set current page value to 1 when any filter changes
  useEffect(() => {
    setPage(1)
  }, [filters])

  useEffect(() => {
		const getUsers = async (currentUser : User) => {
			try {

				const { total, users } = await fetchUsers({
					filters,
					page,
					currentUser,
				});
				setUsers(users);
				setTotal(total);
			} catch (err) {
				swal(
					"Ocurrió un problema",
					"No pudimos obtener la lista de usuarios, por favor reinténtalo",
					"warning"
				);
				errorHandler?.report(
					`Error obteniendo la lista de usuarios: ${err}`
				);
			}
		};
    if (auth?.currentUser) {
		getUsers(auth?.currentUser);
	}

  }, [filters, page, refreshToken]);

  const handleActiveChange = useCallback(
    async (uid: string, active: boolean) => {
      try {
        await setUserActive(uid, active);
        setRefreshToken((n) => n + 1);
      } catch (err) {
        swal(
          "No se pudo cambiar el estado",
          "Intentá de nuevo en unos segundos.",
          "warning",
        );
        errorHandler?.report(`Error cambiando el estado del usuario: ${err}`);
      }
    },
    [],
  );

  const handleRoleChange = useCallback(async (uid: string, roleId: number, action: 'add' | 'remove') => {
    try {
      if (action === 'add') {
        await addUserRole(uid, roleId);
      } else {
        await removeUserRole(uid, roleId);
      }
      // Update local state to reflect the change immediately
      setUsers(prev => prev.map(user => {
        if (user.uid !== uid) return user;
        const roleMap: Record<number, string> = {
          2: 'owner', 3: 'supervisor', 4: 'doctor', 5: 'collaborator',
          7: 'supervisor_operativo', 8: 'supervisor_medico', 9: 'supervisor_linea',
        };
        if (action === 'add') {
          return { ...user, roles: [...user.roles, { id: roleId, role: roleMap[roleId] }] };
        } else {
          // La API borra las líneas junto con el rol de supervisor de línea.
          return {
            ...user,
            roles: user.roles.filter(r => r.id !== roleId),
            ...(roleId === 9 ? { lineas: [] } : {}),
          };
        }
      }));
    } catch (err) {
      swal('Error', 'No se pudo actualizar el rol del usuario', 'error');
      errorHandler?.report(`Error actualizando rol de usuario: ${err}`);
    }
  }, []);

  return (
      <section className="w-full flex flex-col gap-12 mt-4 max-h-96 h-full ">
          <div className="w-full flex justify-between items-center">
              <UsersTableFilters initialState={filtersInitialState} filters={filters} setFilters={setFilters} />
          </div>
          <Table
              users={users}
              total={total}
              filters={filters}
              page={page}
              setPage={setPage}
              isAdmin={isAdmin}
              isSoporte={isSoporte}
              onRoleChange={handleRoleChange}
              onActiveChange={handleActiveChange}
              onEdit={setEditandoUid}
          />
          <EditUserDrawer
              user={users.find((user) => user.uid === editandoUid) ?? null}
              bases={bases}
              esAdmin={isAdmin}
              puedeEditarMatricula={puedeEditarMatricula}
              puedeDarAcceso={isAdmin || isSoporte}
              onClose={() => setEditandoUid(null)}
              onSaved={() => setRefreshToken((n) => n + 1)}
          />
      </section>
  )
}